// public/js/ui/tenant-scope-widget.js
// The sidebar tenant-scope selector (docs/tenant-scope.md) + the compact mobile
// topbar chip. One tenant only → a static label (nothing to switch). Multiple
// tenants / platform roles → a select2 dropdown with every pickable tenant,
// "All tenants" and — platform only — "Unassigned".
import {
  getScope, setScope, getMemberships, isScopeSelectable, isPlatformScope,
  scopeLabel, SCOPE_CHANGE_EVENT,
} from '../modules/tenant-scope.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const ALL_VALUE = '__all__';
const UNASSIGNED_VALUE = '__unassigned__';

function scopeToValue(scope) {
  if (scope.unassigned) return UNASSIGNED_VALUE;
  return scope.tenantId || ALL_VALUE;
}

function valueToScope(v) {
  if (v === ALL_VALUE) return { all: true };
  if (v === UNASSIGNED_VALUE) return { unassigned: true };
  return { tenantId: v };
}

function optionsHtml() {
  const cur = scopeToValue(getScope());
  const opt = (v, label) => `<option value="${esc(v)}" ${v === cur ? 'selected' : ''}>${esc(label)}</option>`;
  let html = opt(ALL_VALUE, 'All tenants');
  if (isPlatformScope()) html += opt(UNASSIGNED_VALUE, 'Unassigned');
  html += getMemberships().map(t => opt(t.tenantId, t.name)).join('');
  return html;
}

// The live <select>, so a scope change can update the SELECTION instead of
// rebuilding the widget. Rebuilding tore the element out from under select2 -
// which leaves its container orphaned in the DOM (six of them after a few
// switches) and could re-fire change on the detached original. Only the option
// LIST needs a rebuild, and that changes only with the membership list.
let _sel = null;
let _sig = null;        // what the current DOM was built from

function destroySelect2() {
  try {
    if (_sel && window.$ && window.$.fn?.select2 && window.$(_sel).data('select2')) {
      window.$(_sel).select2('destroy');
    }
  } catch (e) {}
  _sel = null;
}

/** Point the widget at the current scope without touching the DOM structure. */
function syncSelection() {
  if (!_sel) return;
  const v = scopeToValue(getScope());
  if (_sel.value === v) return;
  _sel.value = v;
  // 'change.select2' repaints select2's own label WITHOUT running our change
  // handler - triggering a plain 'change' here would call setScope again and
  // bounce the scope back and forth.
  try { window.$?.(_sel).trigger('change.select2'); } catch (e) {}
}

function renderSidebar() {
  const host = document.getElementById('tenant-scope-widget');
  if (!host) return;

  // Rebuild only when the option list itself would differ.
  const sig = JSON.stringify([isScopeSelectable(), isPlatformScope(),
                              getMemberships().map(t => t.tenantId)]);
  if (sig === _sig && host.firstChild) { syncSelection(); return; }
  destroySelect2();
  _sig = sig;

  if (!isScopeSelectable()) {
    // Exactly one tenant: a fixed label, no dropdown (nothing to switch to).
    const name = getMemberships()[0]?.name;
    host.innerHTML = name
      ? `<div class="tenant-scope-static"><i class="fa-solid fa-building me-2"></i><span class="tenant-name">${esc(name)}</span></div>`
      : '';
    return;
  }

  host.innerHTML = `
    <label class="tenant-scope-label" for="tenant-scope-select">Tenant</label>
    <select id="tenant-scope-select" class="tenant-name">${optionsHtml()}</select>`;

  const sel = host.querySelector('#tenant-scope-select');
  _sel = sel;
  const onPick = (v) => {
    if (v == null) return;
    // Ignore an echo of the value we already hold: select2 fires change on its
    // own in a few flows, and re-setting the same scope is a wasted re-render.
    if (v === scopeToValue(getScope())) return;
    setScope(valueToScope(v));
  };

  // select2 (searchable) when available; the native select works regardless.
  if (window.$ && window.$.fn?.select2) {
    const $sel = window.$(sel);
    $sel.select2({
      width: '100%',
      dropdownParent: window.$(host),
      minimumResultsForSearch: 6,   // search box only once the list gets long
    });
    $sel.on('change', () => onPick($sel.val()));
  } else {
    sel.addEventListener('change', () => onPick(sel.value));
  }
}

// Prompt shown by tenant-bound settings sections when the scope is NOT a
// single tenant: the sidebar selector is the ONLY tenant choice in the app —
// settings pages no longer carry their own picker.
export function scopePromptHtml(subject = 'these settings') {
  return `
    <div class="alert alert-info d-flex align-items-center gap-3" style="max-width:640px;">
      <i class="fa-solid fa-building fa-lg"></i>
      <div>
        <b>Pick a tenant first.</b><br>
        <span class="small">Select a single tenant in the sidebar (top left) to edit ${subject} — the global tenant scope drives every page, including settings.</span>
      </div>
    </div>`;
}

export function initTenantScopeWidget() {
  renderSidebar();
  // Re-render when the scope changes from elsewhere (deep-link auto-switch).
  // Mobile uses the same sidebar widget (opened via the hamburger) — no
  // separate topbar chip.
  window.addEventListener(SCOPE_CHANGE_EVENT, () => renderSidebar());
  // Memberships can change (demo switch), so renderSidebar still decides
  // whether a rebuild is needed - it just no longer does one every time.
}

export { scopeLabel };
